import React, { forwardRef, useImperativeHandle } from "react";
import { findObjectByKeyValue } from '../utils.js';

const TeamDropdown = forwardRef(({
    props,
    activeItemId = null,
    selectedId = null,
    setId,
    startVal = "Select...",
    showPlaceholder = false
}, ref) => {
    // Accept both activeItemId and selectedId for backward compatibility
    const hasSelectedId = selectedId !== null && selectedId !== undefined;
    const hasActiveItemId = activeItemId !== null && activeItemId !== undefined;
    const activeId = hasSelectedId ? selectedId : (hasActiveItemId ? activeItemId : null);

    const [open, setOpen] = React.useState(false);
    const dropdownRef = React.useRef(null);
    const [listMaxHeight, setListMaxHeight] = React.useState(null);

    // The stylesheet caps the open list at 350px, which can run past the
    // bottom edge of short viewports. Measure the space below the block (the
    // list opens anchored to its top) before the list mounts, so the cap is
    // present at insertion; shrinking it after mount would animate through
    // the stylesheet's max-height transition. The list scrolls internally
    // either way, and the 120px floor keeps it usable if a trigger ever sits
    // very close to the bottom.
    const toggleOpen = () => {
        if (!open && dropdownRef.current) {
            const spaceBelow = window.innerHeight - dropdownRef.current.getBoundingClientRect().top - 12;
            setListMaxHeight(spaceBelow < 350 ? Math.max(spaceBelow, 120) : null);
        }
        setOpen(!open);
    };

    React.useEffect(() => {
        function close(e) {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                setOpen(false);
            }
        }
        if (open) {
            window.addEventListener("click", close);
        }
        return function removeListener() {
            window.removeEventListener("click", close);
        }
    }, [open]);

    // Expose clear method via ref
    useImperativeHandle(ref, () => ({
        clear: () => {
            // Reset to default display
            setOpen(false);
            // Component will re-render with selectedId=null showing default title
        }
    }));

    const chosenItem = activeId !== null && activeId !== undefined
        ? findObjectByKeyValue(props, 'id', activeId)
        : null;
    const defaultTitle = showPlaceholder ? startVal : (props && props.length > 0 ? props[0].title : startVal);
    const title = chosenItem ? chosenItem.title : defaultTitle;

        return (
        <div className="drop-block" ref={dropdownRef}>
            <div className={`c-custom-drop ${open ? "active" : ""}`}>
                <div 
                    className="current" 
                    onClick={toggleOpen}
                    role="button"
                    tabIndex="0"
                    aria-haspopup="listbox"
                    aria-expanded={open}
                    aria-label={title}
                >
                    {title}
                </div>
                {open && (
                    <ul
                        className="drop-list"
                        role="listbox"
                        style={listMaxHeight !== null ? { maxHeight: `${listMaxHeight}px` } : undefined}
                    >
                        {props && props.map((item, key) => (
                            <li 
                                key={key} 
                                className={`${item.id === activeId ? "active" : ""} ${item.disabled ? "disabled" : ""}`}
                                onClick={() => {
                                    if (item.disabled) return;
                                    if (setId) {
                                        setId(item.id);
                                        setOpen(false);
                                    } else {
                                        console.error('[TeamDropdown] Error: setId function is not provided!');
                                    }
                                }} 
                                role="option"
                                aria-selected={item.id === activeId}
                                aria-disabled={!!item.disabled}
            >
                <div className="value">
                    {item.title}
                </div>
                                {item.properties && (
                    <div className="descr">
                        <span className="good">Hint:</span>{item.properties.advantages} <br/>
                        <span className="bad">Trade-off:</span>{item.properties.flaws}
                    </div>
                                )}
            </li>
                        ))}
                </ul>
                )}
            </div>
        </div>
    )
});

export { TeamDropdown };
